/**
 * EAA-1765: `returnTo` resolution for the portal-as-central-login flow.
 *
 * An app bounces an unauthenticated user to `…/login?returnTo=<deep link>`; after
 * Keycloak the portal must send the browser back to that exact link. `returnTo`
 * is attacker-influenced (it rides a query string), so it is ALWAYS validated
 * against the runtime allowlist before we ever navigate to it — this is the
 * open-redirect guard. Anything not clearly allowlisted resolves to the portal
 * launcher, never the raw input.
 */

export type ResolvedReturnTo =
    | { kind: 'external'; url: string } // full URL whose origin is allowlisted → leave the portal
    | { kind: 'internal'; path: string }; // portal-relative path → in-app navigate

/** True only for an absolute http(s) URL whose ORIGIN is in the allowlist. */
export function isAllowlistedExternal(returnTo: string, allowlist: readonly string[]): boolean {
    try {
        const url = new URL(returnTo);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
        return allowlist.includes(url.origin);
    } catch {
        return false; // not an absolute URL
    }
}

/**
 * Resolve a raw `returnTo` into a safe navigation target.
 * - allowlisted absolute URL → external redirect
 * - portal-relative path (`/x`, not `//host`) → internal navigate
 * - anything else (off-allowlist origin, protocol-relative, garbage) → launcher `/`
 */
export function resolveReturnTo(
    returnTo: string | null | undefined,
    allowlist: readonly string[] = []
): ResolvedReturnTo {
    if (!returnTo) return { kind: 'internal', path: '/' };
    if (isAllowlistedExternal(returnTo, allowlist)) {
        return { kind: 'external', url: returnTo };
    }
    // Portal-relative only: a single leading slash, never protocol-relative `//evil`.
    if (returnTo.startsWith('/') && !returnTo.startsWith('//')) {
        return { kind: 'internal', path: returnTo };
    }
    return { kind: 'internal', path: '/' };
}
